import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { useMutation, useQuery, useSubscription } from '@apollo/client';
import noop from 'lodash/noop';

import { useCurrentUserContext } from 'utils/User';

import { USER_EVENT_KINDS } from './constants';
import markUserEventsReadMutation from './hooks/markUserEventsRead.graphql';
import unreadUserEventsQuery from './hooks/unreadUserEvents.graphql';
import userEventReceivedSubscription from './hooks/userEventReceived.graphql';
import { type UserEvent, type UserEventHandler } from './types';

interface UserEventsContextValue {
  registerHandler: (
    kind: UserEventKindEnum,
    handler: UserEventHandler,
  ) => () => void;
}

/* istanbul ignore next -- @preserve */
export const UserEventsContext = createContext<UserEventsContextValue>({
  registerHandler: () => noop,
});

/**
 * Delivers "something you started has finished" events from the API to
 * whichever component knows how to present them.
 *
 * Two sources feed the same dispatch:
 *
 *  - a websocket subscription, for events that arrive while the app is open;
 *  - a query for unread events on mount, which covers everything the socket
 *    couldn't deliver because nobody was listening — a reload or a closed tab.
 *
 * Consumers register with `useUserEventHandler` and report whether they
 * consumed the event; consumed events are marked read so they don't come back.
 */
const UserEventsProvider = ({ children }: { children: ReactNode }) => {
  const { isLoggedIn } = useCurrentUserContext();

  // `CurrentUserProvider` renders <Loading /> until its query settles, so a
  // mounted child always sees a real boolean. `undefined` means this provider
  // was rendered outside that tree, where every query below is skipped and
  // nothing is ever delivered — silently. Say so instead.
  if (isLoggedIn === undefined) {
    // eslint-disable-next-line no-console
    console.error(
      'UserEventsProvider must be rendered inside CurrentUserProvider; ' +
        'user events will not be delivered.',
    );
  }

  const handlersRef = useRef(new Map<string, Set<UserEventHandler>>());
  // Events already consumed or currently being handled. The socket and the
  // catch-up query overlap by design, so the same event routinely arrives
  // twice and must only be acted on once.
  const claimedRef = useRef(new Set<string>());

  const [markUserEventsRead] = useMutation<
    MarkUserEventsReadMutation,
    MarkUserEventsReadMutationVariables
  >(markUserEventsReadMutation);

  const registerHandler = useCallback(
    (kind: UserEventKindEnum, handler: UserEventHandler) => {
      const forKind = handlersRef.current.get(kind) ?? new Set();
      forKind.add(handler);
      handlersRef.current.set(kind, forKind);

      return () => {
        forKind.delete(handler);
      };
    },
    [],
  );

  const dispatch = useCallback(
    async (event?: UserEvent | null) => {
      if (!event || claimedRef.current.has(event.id)) return;

      const handlers = handlersRef.current.get(event.kind);
      // Nothing here knows what this is. Leave it unread rather than
      // swallowing it, so a page that does — or a later release — still can.
      if (!handlers?.size) return;

      claimedRef.current.add(event.id);

      // Each handler is isolated: the async wrapper turns a synchronous throw
      // into a rejection, and `allSettled` means one failure can't discard
      // another handler's success. With `Promise.all` a single rejection would
      // mark the whole event unconsumed, so an event that *was* already
      // presented would come back and be presented twice.
      const results = await Promise.allSettled(
        [...handlers].map(async (handler) => handler(event)),
      );
      const consumed = results.some(
        (result) => result.status === 'fulfilled' && Boolean(result.value),
      );

      if (!consumed) {
        // Release the claim so the next catch-up can retry it.
        claimedRef.current.delete(event.id);
        return;
      }

      // Best effort. If this fails the event stays unread and is redelivered
      // later, which beats losing it.
      markUserEventsRead({ variables: { ids: [event.id] } }).catch(noop);
    },
    [markUserEventsRead],
  );

  const { data } = useQuery<UnreadUserEventsQuery>(unreadUserEventsQuery, {
    skip: !isLoggedIn,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    data?.unreadUserEvents?.forEach((event) => dispatch(event));
  }, [data, dispatch]);

  useSubscription<UserEventReceivedSubscription>(
    userEventReceivedSubscription,
    {
      skip: !isLoggedIn,
      onData: ({ data: result }) =>
        dispatch(result.data?.userEventReceived?.event),
    },
  );

  const value = useMemo(() => ({ registerHandler }), [registerHandler]);

  return (
    <UserEventsContext.Provider value={value}>
      {children}
    </UserEventsContext.Provider>
  );
};

/**
 * Handle every user event of `kind`. The handler doesn't need to be memoized —
 * the latest one is always used.
 */
export function useUserEventHandler(
  kind: UserEventKindEnum,
  handler: UserEventHandler,
): void {
  const { registerHandler } = useContext(UserEventsContext);

  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(
    () => registerHandler(kind, (event) => handlerRef.current(event)),
    [kind, registerHandler],
  );
}

export { USER_EVENT_KINDS };
export default UserEventsProvider;
